import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Bot, Ruler } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const products = [
  {
    icon: Bot,
    name: 'VULCAN SERIES',
    title: 'Autonomous Pipe Inspection Robots',
    description:
      'Next-generation robotics for extreme industrial environments. The Vulcan Series brings autonomous intelligence to pipeline inspection and industrial surveying.',
    highlights: [
      'AI-powered navigation with multi-sensor fusion',
      '5 km operational range with 24/7 capability',
      'Real-time data via fiber-optic tether',
    ],
    image:
      'https://images.unsplash.com/photo-1677442135703-1787eea5ce01?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHxBSSUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzY2NzkyODc3fDA&ixlib=rb-4.1.0&q=85',
    path: '/vulcan',
    cta: 'Explore Vulcan',
  },
  {
    icon: Ruler,
    name: 'ASTRA SERIES',
    title: 'Precision Field Measurement Devices',
    description:
      'Affordable, tamper-proof measurement devices with direct CAD output. Built for civil infrastructure contractors who need accuracy they can trust.',
    highlights: [
      '0.038 mm encoder resolution with 14-bit precision',
      'Direct DWG/DXF CAD output — no manual drafting',
      'Cryptographically signed, tamper-proof data',
    ],
    image:
      'https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?crop=entropy&cs=srgb&fm=jpg&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8c3VydmV5aW5nfGVufDB8fDB8fHww&ixlib=rb-4.1.0&q=85',
    path: '/astra',
    cta: 'Explore Astra',
  },
];

const ProductsSection = () => {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  return (
    <section id="products" className="py-24 px-6 relative overflow-hidden">
      <div
        className="absolute top-0 left-0 w-96 h-96 rounded-full blur-3xl opacity-10"
        style={{ background: '#F97316' }}
      />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground-primary">
            Our Products
          </h2>
          <p className="text-lg max-w-2xl mx-auto text-foreground-secondary">
            Purpose-built technology for the field
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {products.map((product, index) => (
            <Link
              key={index}
              to={product.path}
              className="glass-frosted rounded-3xl overflow-hidden transition-smooth glow-subtle group block"
            >
              <div className="relative h-56 overflow-hidden">
                <img
                  src={product.image}
                  alt={product.title}
                  className="w-full h-full object-cover transition-smooth group-hover:brightness-110"
                />
                <div
                  className="absolute inset-0"
                  style={{
                    background: isLight
                      ? 'linear-gradient(180deg, transparent 0%, rgba(245, 245, 247, 0.9) 100%)'
                      : 'linear-gradient(180deg, transparent 0%, rgba(11, 14, 19, 0.8) 100%)',
                  }}
                />
                <div className="absolute bottom-4 left-6">
                  <span
                    className="text-xs font-bold tracking-widest"
                    style={{ color: '#F97316' }}
                  >
                    {product.name}
                  </span>
                </div>
              </div>

              <div className="p-8 space-y-4">
                <h3 className="text-2xl font-bold text-foreground-primary">
                  {product.title}
                </h3>
                <p className="text-sm leading-relaxed text-foreground-secondary">
                  {product.description}
                </p>

                <ul className="space-y-2">
                  {product.highlights.map((h, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-2 h-2 rounded-full bg-orange-500 mt-1.5 flex-shrink-0" />
                      <span className="text-sm text-foreground-secondary">
                        {h}
                      </span>
                    </li>
                  ))}
                </ul>

                <div className="pt-2">
                  <span
                    className="inline-flex items-center text-sm font-semibold transition-smooth group-hover:translate-x-1"
                    style={{ color: '#F97316' }}
                  >
                    {product.cta}
                    <ArrowRight className="ml-2" size={16} />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProductsSection;
